import React from 'react';
import { TrendingUp } from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const RevenueTrendChart = ({ monthlyData, comparisonData }) => {
    // Handle empty or invalid data
    const hasData = monthlyData && monthlyData.length > 0 && monthlyData.some(item => item.revenue > 0);

    if (!hasData) {
        return (
            <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                            <TrendingUp className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                        </div>
                        <span className="text-foreground/90 font-medium">Revenue Trend</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px] flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                            <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p className="text-sm">No revenue data available</p>
                            <p className="text-xs text-muted-foreground/70 mt-1">Start generating revenue to see trends</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        <TrendingUp className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                    </div>
                    <span className="text-foreground/90 font-medium">Revenue Trend</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyData}>
                            <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
                                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorRevenueComparison" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                className="stroke-border/20 dark:stroke-border/10"
                                vertical={false}
                                stroke="hsl(var(--border))"
                            />
                            <XAxis
                                dataKey="name"
                                className="text-xs"
                                tick={{ fill: 'hsl(var(--muted-foreground) / 0.6)' }}
                                stroke="hsl(var(--border) / 0.1)"
                                tickLine={false}
                            />
                            <YAxis
                                className="text-xs"
                                tick={{ fill: 'hsl(var(--muted-foreground) / 0.6)' }}
                                stroke="hsl(var(--border) / 0.1)"
                                tickLine={false}
                                tickFormatter={(value) => `$${Math.round(value).toLocaleString()}`}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        const currentValue = payload[0].value;
                                        const currentDate = monthlyData.find(d => d.name === label)?.date;
                                        const previousValue = comparisonData.find(d => d.name === label)?.revenue || 0;
                                        const previousDate = comparisonData.find(d => d.name === label)?.date;

                                        // Calculate change percentage safely
                                        let changeText = 'N/A';
                                        if (previousValue > 0) {
                                            const change = ((currentValue - previousValue) / previousValue) * 100;
                                            changeText = `${change >= 0 ? '↑' : '↓'} ${Math.round(Math.abs(change))}%`;
                                        } else if (currentValue > 0) {
                                            changeText = '↑ New';
                                        }

                                        return (
                                            <div className="rounded-lg border border-border/20 bg-card/95 backdrop-blur-sm p-3 shadow-lg">
                                                <p className="text-sm font-medium text-foreground/95 dark:text-foreground/90">{currentDate}</p>
                                                <div className="mt-2 space-y-1">
                                                    <div className="flex items-center justify-between gap-4">
                                                        <span className="text-xs text-muted-foreground/80 dark:text-muted-foreground/70">Current</span>
                                                        <span className="text-sm font-medium text-foreground/95 dark:text-foreground/90">
                                                            ${Math.round(currentValue).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    {previousValue > 0 && (
                                                        <div className="flex items-center justify-between gap-4">
                                                            <span className="text-xs text-muted-foreground/80 dark:text-muted-foreground/70">Previous ({previousDate})</span>
                                                            <span className="text-sm font-medium text-foreground/80 dark:text-foreground/70">
                                                                ${Math.round(previousValue).toLocaleString()}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="flex items-center justify-between gap-4">
                                                        <span className="text-xs text-muted-foreground/80 dark:text-muted-foreground/70">Change</span>
                                                        <span className="text-sm font-medium text-muted-foreground/90">
                                                            {changeText}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="revenue"
                                stroke="hsl(var(--primary))"
                                strokeWidth={2}
                                fill="url(#colorRevenue)"
                                activeDot={{
                                    r: 6,
                                    stroke: "hsl(var(--primary))",
                                    strokeWidth: 2,
                                    fill: "hsl(var(--card))",
                                }}
                            />
                            <Area
                                type="monotone"
                                data={comparisonData}
                                dataKey="revenue"
                                stroke="hsl(var(--muted-foreground))"
                                strokeWidth={1}
                                strokeDasharray="4 4"
                                fill="url(#colorRevenueComparison)"
                                activeDot={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default RevenueTrendChart; 